import type { ManualDoc } from "../types.ts"
import { t } from "./t.ts"

export const WAREHOUSE_DOCS: ManualDoc[] = [
  {
    slug: "scan",
    category: "warehouse",
    order: 1,
    title: t("입고 스캔", "入庫スキャン"),
    summary: t(
      "상자 바코드를 찍어 입고 처리하고, 이어서 중량과 착불비를 적습니다.",
      "箱のバーコードを読み取って入庫し、続けて重量と着払い費用を記入します。",
    ),
    roles: ["warehouse", "admin"],
    keywords: ["스캔", "바코드", "입고", "중량", "착불", "카메라", "무적화물", "잇기", "スキャン", "入庫", "重量", "着払い", "未登録貨物", "紐づけ"],
    body: [
      {
        kind: "p",
        text: t(
          "상자가 창고에 도착하면 이 화면에서 번호를 찍습니다. 찍는 순간 그 반품 건이 입고완료가 되고, 바로 아래에 중량과 착/선불을 적는 칸이 열립니다.",
          "箱が倉庫に届いたらこの画面で番号を読み取ります。読み取った時点でその返品が入庫完了になり、すぐ下に重量と着払い/元払いを入力する欄が開きます。",
        ),
      },
      { kind: "heading", text: t("바코드 스캐너로", "バーコードスキャナーで") },
      {
        kind: "steps",
        items: [
          t("왼쪽 메뉴에서 입고 스캔을 엽니다.", "左メニューから入庫スキャンを開きます。"),
          t(
            "입력칸에 커서가 자동으로 들어가 있습니다. 스캐너로 상자의 반송장 바코드를 찍습니다.",
            "入力欄に自動でカーソルが入っています。スキャナーで箱の返送伝票バーコードを読み取ります。",
          ),
          t("중량을 그램(g)으로 칩니다.", "重量をグラム(g)で入力します。"),
          t(
            "착/선불은 착불로 미리 골라져 있습니다. 착불비(JPY)를 적고, 모르면 비워 둡니다. 선불 상자만 바꿔 고릅니다.",
            "着払い/元払いはあらかじめ着払いが選ばれています。着払い費用(JPY)を入力し、分からなければ空欄にします。元払いの箱だけ選び直します。",
          ),
          t("저장을 누릅니다. 다음 상자를 바로 찍을 수 있습니다.", "保存を押します。続けて次の箱を読み取れます。"),
        ],
      },      {
        kind: "image",
        src: t("/help/scan-ko.png", "/help/scan-ja.png"),
        alt: t("입고 스캔 화면", "入庫スキャンの画面"),
        caption: t(
          "입력칸에 커서가 늘 들어가 있어 스캐너로 바로 찍을 수 있습니다. 아래에 오늘 찍은 건이 쌓입니다.",
          "入力欄に常にカーソルがあり、スキャナーですぐ読み取れます。下に本日読み取った件が並びます。",
        ),
      },

      {
        kind: "note",
        tone: "info",
        text: t(
          "중량을 나중에 적으려면 나중에를 누르고 다음 상자로 넘어가면 됩니다. 중량은 반품 상세에서 언제든 적을 수 있습니다.",
          "重量を後で入力する場合は「後で」を押して次の箱に進みます。重量は返品詳細からいつでも入力できます。",
        ),
      },
      { kind: "heading", text: t("휴대폰 카메라로", "スマートフォンのカメラで") },
      {
        kind: "steps",
        items: [
          t("입고 스캔 화면에서 카메라로 스캔을 누릅니다.", "入庫スキャン画面でカメラでスキャンを押します。"),
          t("바코드를 안내선 안에 맞춥니다. 어두우면 플래시를 켭니다.", "バーコードを枠の中に合わせます。暗い場合はフラッシュを点けます。"),
          t(
            "읽히면 그대로 입고됩니다. 이어서 중량·착선불을 적습니다.",
            "読み取れるとそのまま入庫されます。続けて重量・着払い/元払いを入力します。",
          ),
        ],
      },
      {
        kind: "list",
        items: [
          t("카메라는 https 로 접속했을 때만 쓸 수 있습니다.", "カメラはhttpsで接続した場合のみ使用できます。"),
          t(
            "잘 읽히지 않으면 아래 칸에 번호를 직접 쳐도 똑같이 입고됩니다.",
            "うまく読み取れない場合は、下の欄に番号を直接入力しても同じように入庫できます。",
          ),
          t("한동안 쓰지 않으면 카메라가 저절로 꺼집니다. 다시 켜기를 누르면 됩니다.", "しばらく使わないとカメラは自動で切れます。再度点けるボタンで再開できます。"),
        ],
      },
      { kind: "heading", text: t("찍었는데 안 될 때", "読み取れないとき") },
      {
        kind: "table",
        head: [t("나오는 말", "表示"), t("뜻과 할 일", "意味と対応")],
        rows: [
          [
            t("해당 운송장번호의 반품 건이 없습니다.", "該当する伝票番号の返品がありません。"),
            t(
              "아직 접수되지 않았거나, 접수할 때 번호를 비워 둔 건입니다. 반품 목록에서 주문번호·고객명으로 찾아 손으로 입고 처리를 하세요.",
              "まだ受付されていないか、受付時に番号が空欄の件です。返品一覧で注文番号・顧客名から探し、手入力で入庫してください。",
            ),
          ],
          [
            t("이미 입고 처리된 건입니다.", "すでに入庫処理された件です。"),
            t(
              "같은 상자를 두 번 찍었거나 번호가 겹칩니다. 반품 상세에서 상태를 확인하세요.",
              "同じ箱を2回読み取ったか、番号が重複しています。返品詳細で状態を確認してください。",
            ),
          ],
        ],
      },
      {
        kind: "p",
        text: t(
          "화면 아래 오늘 스캔에 오늘 찍은 건이 쌓입니다. 줄을 누르면 그 건의 상세가 열립니다.",
          "画面下の「本日のスキャン」に本日読み取った件が並びます。行を押すとその件の詳細が開きます。",
        ),
      },
      { kind: "heading", text: t("무적화물 — 접수가 없는 상자", "未登録貨物 — 受付のない荷物") },
      {
        kind: "p",
        text: t(
          "찍은 번호로 접수된 건이 없고 그 번호가 사가와·야마토 운송장 형식이면 무적화물로 적힙니다. 상자는 손에 있으니 실패가 아닙니다 — 나중에 어느 건인지 밝혀지면 그 건에 잇습니다.",
          "読み取った番号で受付が見つからず、その番号が佐川・ヤマトの送り状形式であれば未登録貨物として記録されます。荷物は手元にあるので失敗ではありません — 後でどの案件か分かったら、その案件に紐づけます。",
        ),
      },
      {
        kind: "list",
        items: [
          t(
            "입고 스캔 화면의 무적화물 카드에서 줄을 누르면 이을 건을 고르는 옆판이 열립니다.",
            "入庫スキャン画面の未登録貨物カードで行を押すと、紐づける案件を選ぶパネルが開きます。",
          ),
          t(
            "왼쪽 메뉴의 무적화물에서도 똑같이 이을 수 있습니다. 이쪽은 이미 이은 기록까지 찾아볼 수 있습니다.",
            "左メニューの未登録貨物からも同じように紐づけられます。こちらは紐づけ済みの記録まで検索できます。",
          ),
          t(
            "이으면 그 건에 운송장번호가 채워지고 입고완료가 됩니다. 반송장번호가 비어 있는 접수 건만 고를 수 있습니다.",
            "紐づけると、その案件に送り状番号が入り入庫完了になります。返送伝票番号が空の受付案件のみ選べます。",
          ),
          t(
            "같은 번호로 접수가 뒤늦게 올라왔다면, 상자를 다시 찍는 것만으로 저절로 풀립니다.",
            "同じ番号で受付が後から登録された場合は、箱を再度スキャンするだけで自動的に解消されます。",
          ),
        ],
      },
    ],
  },

  {
    slug: "manual-arrival",
    category: "warehouse",
    order: 2,
    title: t("손으로 입고 처리", "手入力で入庫"),
    summary: t(
      "반송장번호가 없어 스캔으로 찾을 수 없는 상자를 입고합니다.",
      "返送伝票番号がなくスキャンで見つけられない箱を入庫します。",
    ),
    roles: ["warehouse", "admin"],
    keywords: ["손입고", "수동", "번호없음", "확인중", "手入力", "入庫"],
    body: [
      {
        kind: "p",
        text: t(
          "접수할 때 반송장번호를 몰라 비워 둔 건이 있습니다. 그런 건은 스캔으로 찾히지 않으므로 반품 상세에서 손으로 입고합니다.",
          "受付時に返送伝票番号が分からず空欄にした件があります。そうした件はスキャンで見つからないため、返品詳細から手で入庫します。",
        ),
      },
      {
        kind: "steps",
        items: [
          t(
            "반품 목록에서 주문번호나 고객명으로 그 건을 찾습니다(위쪽 통합 검색을 써도 됩니다).",
            "返品一覧で注文番号や顧客名から該当の件を探します(上部の統合検索でも構いません)。",
          ),
          t("반품 상세를 엽니다. 상태가 접수완료여야 합니다.", "返品詳細を開きます。状態が受付完了である必要があります。"),
          t(
            "지금 할 일에서 반송장번호 칸에 상자에 적힌 번호를 칩니다(번호가 이미 있으면 이 칸은 나오지 않습니다).",
            "「今やること」の返送伝票番号欄に、箱に書かれた番号を入力します(既に番号がある場合この欄は表示されません)。",
          ),
          t("손으로 입고 처리를 누릅니다.", "手入力で入庫を押します。"),
        ],
      },
      {
        kind: "list",
        items: [
          t("상태가 입고완료가 되고 반입일이 오늘로 찍힙니다.", "状態が入庫完了になり、入庫日が本日で記録されます。"),
          t("적어 넣은 번호로 이후에는 검색과 스캔이 걸립니다.", "入力した番号で、以後は検索もスキャンもできるようになります。"),
          t("이력에는 손 입고 처리로 남아 스캔과 구분됩니다.", "履歴には「手入力で入庫」として残り、スキャンと区別されます。"),
        ],
      },
      {
        kind: "note",
        tone: "warn",
        text: t(
          "이미 다른 번호가 적혀 있는 건에 다른 번호를 넣으면 막습니다. 다른 화주 건이 같은 번호를 쓰고 있어도 막습니다 — 번호를 다시 확인하세요.",
          "既に別の番号が入っている件に違う番号を入れると止まります。他の荷主の件が同じ番号を使っている場合も止まります — 番号を確認してください。",
        ),
      },
    ],
  },

  {
    slug: "inspect",
    category: "warehouse",
    order: 3,
    title: t("검수 — 반품 상품 확인", "検品 — 返品商品の確認"),
    summary: t(
      "상자를 열어 줄마다 실제로 몇 개 왔는지 셉니다. 검수해야 서류를 확정할 수 있습니다.",
      "箱を開けて行ごとに実際に何点届いたかを数えます。検品しないと書類を確定できません。",
    ),
    roles: ["warehouse", "admin"],
    keywords: ["검수", "수량", "불일치", "확인", "検品", "数量", "不一致"],
    body: [
      {
        kind: "p",
        text: t(
          "화주가 접수할 때 무엇이 몇 개 오는지 적어 두었습니다. 상자를 열어 그 수량과 실물을 맞춰 보는 일이 검수입니다.",
          "荷主が受付時に「何が何点戻るか」を記入しています。箱を開けてその数量と現物を突き合わせるのが検品です。",
        ),
      },
      {
        kind: "steps",
        items: [
          t("입고한 건의 반품 상세를 엽니다(입고 스캔 화면의 오늘 스캔에서 바로 열 수 있습니다).", "入庫した件の返品詳細を開きます(入庫スキャン画面の「本日のスキャン」からすぐ開けます)。"),
          t(
            "지금 할 일에 줄마다 칸이 하나씩 나옵니다. 상품명 옆에 등록된 반품수량이 함께 적혀 있습니다.",
            "「今やること」に行ごとに入力欄が表示されます。商品名の横に登録された返品数量が併記されています。",
          ),
          t("줄마다 실제로 온 개수를 칩니다. 하나도 안 왔으면 0 을 칩니다.", "行ごとに実際に届いた数を入力します。1点も届いていなければ 0 を入力します。"),
          t("다른 점이 있으면 검수 메모에 적습니다.", "相違がある場合は検品メモに記入します。"),
          t("검수 결과 저장을 누릅니다.", "検品結果を保存を押します。"),
        ],
      },      {
        kind: "image",
        src: t("/help/inspect-ko.png", "/help/inspect-ja.png"),
        alt: t("검수 입력 칸", "検品の入力欄"),
        caption: t(
          "줄마다 등록된 반품수량이 함께 적혀 있어 실물과 견주기 좋습니다. 중량·착불비도 같은 자리에서 적습니다.",
          "行ごとに登録された返品数量が併記されており、現物と突き合わせやすくなっています。重量・着払い費用も同じ場所で入力します。",
        ),
      },

      {
        kind: "note",
        tone: "warn",
        text: t(
          "모든 줄을 세야 저장됩니다. 일부만 적으면 아직 안 봤다와 0개 왔다를 구별할 수 없기 때문입니다.",
          "すべての行を入力しないと保存できません。一部だけでは「まだ見ていない」と「0点だった」を区別できないためです。",
        ),
      },
      { kind: "heading", text: t("수량이 다를 때", "数量が合わないとき") },
      {
        kind: "list",
        items: [
          t("수량이 달라도 저장은 됩니다. 검수는 사실을 적는 일이고, 어떻게 할지는 화주가 정합니다.", "数量が違っても保存できます。検品は事実を記録する作業で、対応は荷主が決めます。"),
          t("다른 점은 이력에 남고, 화주의 할 일에 수량 불일치로 뜹니다.", "相違は履歴に残り、荷主の対応待ちに「数量不一致」として表示されます。"),
          t("반품 상품 표에서 검수수량이 빨갛게 보입니다.", "返品商品の表で検品数量が赤く表示されます。"),
        ],
      },
      {
        kind: "p",
        text: t(
          "잘못 셌으면 다시 저장하면 됩니다. 마지막에 저장한 값이 남습니다.",
          "数え間違えた場合は再度保存すれば上書きされます。最後に保存した値が残ります。",
        ),
      },
    ],
  },

  {
    slug: "invoice",
    category: "warehouse",
    order: 4,
    title: t("인보이스 품목 작성", "インボイス品目の記入"),
    summary: t(
      "세관에 낼 인보이스에 들어갈 품명·HS코드·원산지·수량·단가를 적습니다.",
      "税関に提出するインボイスに載せる品名・HSコード・原産地・数量・単価を記入します。",
    ),
    roles: ["warehouse", "admin"],
    keywords: ["인보이스", "품목", "HS코드", "원산지", "단가", "통화", "インボイス", "品目", "原産地"],
    body: [
      {
        kind: "p",
        text: t(
          "한국으로 되돌려 보내는 건은 세관 서류가 필요합니다. 상자를 열어 실물을 본 쪽이 적는 것이 정확하므로 이 일은 쿨트랙JP가 합니다.",
          "韓国へ返送する件には税関書類が必要です。箱を開けて現物を見た側が記入するのが正確なため、この作業は日本側が行います。",
        ),
      },
      {
        kind: "steps",
        items: [
          t("반품 상세에서 인보이스 품목 탭을 엽니다.", "返品詳細でインボイス品目タブを開きます。"),
          t(
            "품목 마스터에서 고르기에서 품명이나 HS코드로 찾아 줄을 더합니다. 목록에 없으면 직접 입력으로 빈 줄을 만듭니다.",
            "「品目マスタから選ぶ」で品名やHSコードから探して行を追加します。一覧にない場合は「直接入力」で空の行を作ります。",
          ),
          t(
            "줄마다 품명(영문)·HS코드·원산지·수량·단가를 확인하고 고칩니다. 마스터에서 고르면 대부분 채워져 있습니다.",
            "行ごとに品名(英語)・HSコード・原産地・数量・単価を確認して修正します。マスタから選べばほとんど埋まっています。",
          ),
          t("품목 저장을 누릅니다.", "品目を保存を押します。"),
        ],
      },      {
        kind: "image",
        src: t("/help/invoice-items-ko.png", "/help/invoice-items-ja.png"),
        alt: t("인보이스 품목 탭", "インボイス品目タブ"),
        caption: t(
          "위쪽 표가 신고할 품목이고, 아래에서 품목 마스터를 검색해 줄을 더합니다.",
          "上の表が申告する品目で、下で品目マスタを検索して行を追加します。",
        ),
      },

      {
        kind: "list",
        items: [
          t(
            "저장하면 서류작성여부가 인보이스 항목기재가 되고, 화주의 할 일에 서류 확인 대기로 뜹니다.",
            "保存すると書類作成状況が「インボイス品目記入」になり、荷主の対応待ちに「書類確認待ち」として表示されます。",
          ),
          t(
            "품명·HS코드·원산지는 고른 그 시점의 값을 복사해 둡니다. 나중에 품목 마스터를 고쳐도 이미 신고한 내용은 바뀌지 않습니다.",
            "品名・HSコード・原産地は選んだ時点の値をコピーして保存します。後で品目マスタを変更しても、申告済みの内容は変わりません。",
          ),
          t("한 인보이스에 여러 통화를 섞을 수 없습니다.", "1つのインボイスに複数の通貨を混在させることはできません。"),
        ],
      },
      { kind: "heading", text: t("인보이스 파일 내려받기", "インボイスのダウンロード") },
      {
        kind: "p",
        text: t(
          "품목은 건마다 적지만 인보이스 파일은 선적 단위로 만듭니다. 선적 상세에서 인보이스 내려받기를 누르면 XLSX 로 받습니다. 처음 받을 때 인보이스번호가 붙고 그 뒤로는 바뀌지 않습니다.",
          "品目は件ごとに記入しますが、インボイスファイルは船積み単位で作成します。船積み詳細で「インボイスをダウンロード」を押すとXLSXで取得できます。初回のダウンロード時にインボイス番号が確定し、その後は変わりません。",
        ),
      },
      {
        kind: "note",
        tone: "info",
        text: t(
          "화주가 항목 확인요청을 걸면 서류가 확정되지 않습니다. 요청 이유를 보고 품목이나 중량·착선불을 고쳐 다시 저장하면 풀립니다.",
          "荷主が項目の確認依頼を出すと書類は確定できません。依頼理由を確認し、品目または重量・着払/元払を修正して保存すると解除されます。",
        ),
      },
    ],
  },

  {
    slug: "shipment",
    category: "warehouse",
    order: 5,
    title: t("선적과 발송", "船積みと発送"),
    summary: t(
      "서류가 끝난 건을 묶어 도쿄에서 내보내고, 오사카를 거쳐 한국으로 띄웁니다.",
      "書類が完了した件をまとめて東京から出庫し、大阪を経て韓国へ送ります。",
    ),
    roles: ["warehouse", "admin"],
    keywords: ["선적", "출고", "세이노", "오사카", "팬스타", "출항", "船積み", "出庫", "西濃", "パンスター"],
    body: [
      {
        kind: "p",
        text: t(
          "서류완료가 된 건만 선적에 실을 수 있습니다. 여러 화주의 건을 한 선적에 섞어 보낼 수 있습니다.",
          "書類完了になった件だけを船積みに載せられます。複数の荷主の件を1つの船積みに混載できます。",
        ),
      },
      { kind: "heading", text: t("선적 만들기", "船積みの作成") },
      {
        kind: "steps",
        items: [
          t("왼쪽 메뉴에서 선적을 엽니다.", "左メニューから船積みを開きます。"),
          t("출고 대기 중인 반품 건 표에서 실을 건을 체크합니다.", "「出庫待ちの返品」の表で載せる件にチェックを入れます。"),
          t("선택한 건으로 선적 만들기를 누릅니다.", "「選んだ件で船積みを作成」を押します。"),
          t("목록 맨 위에 새 선적이 생깁니다. 줄을 눌러 상세로 들어갑니다.", "一覧の先頭に新しい船積みができます。行を押して詳細に進みます。"),
        ],
      },      {
        kind: "image",
        src: t("/help/shipment-ko.png", "/help/shipment-ja.png"),
        alt: t("선적 상세 화면", "船積み詳細の画面"),
        caption: t(
          "위쪽 단계 표시로 지금 어디까지 왔는지 보이고, 다음에 할 일만 카드로 나옵니다.",
          "上部のステップで現在地が分かり、次にやることだけがカードで表示されます。",
        ),
      },

      { kind: "heading", text: t("보내는 순서", "発送の手順") },
      {
        kind: "table",
        head: [t("단추", "ボタン"), t("적는 값", "入力値"), t("바뀌는 상태", "変わる状態")],
        rows: [
          [t("출고 처리", "出庫処理"), t("출고일, 세이노번호, 박스수", "出庫日、西濃番号、箱数"), t("선적: 출고 · 반품 건: 출고", "船積み: 出庫 · 返品: 出庫")],
          [t("오사카 도착 기록", "大阪着を記録"), t("오사카 도착일, 항차(선택)", "大阪到着日、配船日(任意)"), t("선적: 오사카 도착", "船積み: 大阪到着")],
          [t("일본 출항 처리", "日本出航を記録"), t("팬스타 추적번호, 항차(선택)", "パンスター追跡番号、配船日(任意)"), t("선적: 해상 운송중", "船積み: 海上輸送中")],
          [t("한국 입고 처리", "韓国入庫を記録"), t("한국 입고일", "韓国入庫日"), t("선적·반품 건: 반송완료 (화주가 찍습니다)", "船積み・返品: 返送完了 (荷主が記録)")],
        ],
      },
      {
        kind: "note",
        tone: "warn",
        text: t(
          "오사카 도착에서 곧바로 종결할 수는 없습니다. 팬스타 추적번호를 적는 일본 출항 처리를 반드시 거칩니다 — 무엇에 실어 보냈는지 남지 않으면 나중에 찾을 수 없기 때문입니다.",
          "大阪到着から直接完了にすることはできません。パンスター追跡番号を記入する「日本出航を記録」を必ず経由します — 何に載せて送ったかが残らないと後から追えないためです。",
        ),
      },
      {
        kind: "list",
        items: [
          t("선적 안의 건은 단계가 바뀔 때마다 함께 움직입니다.", "船積み内の件は、段階が変わるたびに一緒に進みます。"),
          t("한 건을 두 선적에 넣을 수는 없습니다.", "1件を2つの船積みに入れることはできません。"),
          t(
            "화주 계정에는 자기 화주의 건이 든 선적만 보이고, 건수·총중량도 자기 몫만 보입니다.",
            "荷主アカウントには自分の荷主の件が含まれる船積みだけが表示され、件数・総重量も自分の分のみ表示されます。",
          ),
        ],
      },
    ],
  },

  {
    slug: "discard",
    category: "warehouse",
    order: 6,
    title: t("폐기 처리", "廃棄処理"),
    summary: t(
      "한국으로 보내지 않고 일본에서 버리기로 한 건을 종결합니다.",
      "韓国へ送らず日本で廃棄すると決めた件を完了にします。",
    ),
    roles: ["warehouse", "admin"],
    keywords: ["폐기", "버림", "종결", "廃棄", "完了"],
    body: [
      {
        kind: "p",
        text: t(
          "화주가 처리방안을 폐기로 정한 건만 폐기할 수 있습니다. 폐기 건은 인보이스 품목이 필요 없습니다.",
          "荷主が処理方針を廃棄に決めた件のみ廃棄できます。廃棄の件にインボイス品目は不要です。",
        ),
      },
      {
        kind: "steps",
        items: [
          t("반품 상세를 엽니다. 처리방안이 폐기여야 합니다.", "返品詳細を開きます。処理方針が廃棄である必要があります。"),
          t("지금 할 일에서 처리일을 고릅니다.", "「今やること」で処理日を選びます。"),
          t("폐기 처리를 누릅니다.", "廃棄処理を押します。"),
        ],
      },
      {
        kind: "list",
        items: [
          t("상태가 폐기가 되고 그 건은 종결됩니다.", "状態が廃棄になり、その件は完了します。"),
          t("종결된 건은 선적에 실을 수 없습니다.", "完了した件は船積みに載せられません。"),
        ],
      },
    ],
  },
]
